import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getTaxes, saveTax } from "../../components/Api/GmsTax";
import { Eye, SquarePen, Calculator } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  tax_Id: null,
  tax_Code: "",
  tax_NameEn: "",
  tax_NameAr: "",
  tax_Perc: 0,
  tax_Active: true,
  tax_CreatedDate: new Date().toISOString(),
  tax_CreatedBy: 1,
  tax_ModifiedDate: null,
  tax_ModifiedBy: null,
};

const emptyFilter = {
  tax_Code: "",
  tax_NameEn: "",
  tax_NameAr: "",
  tax_Perc: null,
  tax_Active: null,
};

const TaxPage = () => {
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsTax');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsTax');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsTax');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsTax');
  const isAdmin = isUserAdmin();

  useEffect(() => {
    if (hasPermission) {
      loadTaxes();
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    }
  }, [hasPermission, isViewOnly]);

  const loadTaxes = async () => {
    try {
      setLoading(true);
      const data = await getTaxes(filter);
      setTaxes(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!form.tax_NameEn.trim()) {
        Swal.fire("Error", "Tax Name (English) is required", "error");
        return;
      }

      if (form.tax_Perc < 0 || form.tax_Perc > 100) {
        Swal.fire("Error", "Tax Percentage must be between 0 and 100", "error");
        return;
      }

      setLoading(true);
      
      // Create a copy of the form data, excluding tax_Id for new records
      const taxData = { ...form };
      if (!isEditing) {
        delete taxData.tax_Id;
      }
      
      // Ensure active=true by default for users without delete permission
      if (!hasDeletePermission) {
        taxData.tax_Active = true;
      }
      
      const result = await saveTax(taxData);
      
      if (result.id > 0) {
        Swal.fire("Success", result.message || "Tax saved successfully", "success");
        resetForm();
        loadTaxes();
      } else {
        Swal.fire("Error", result.message || "Failed to save tax", "error");
      }
    } catch (error) {
      console.error("Error saving tax:", error);
      Swal.fire("Error", "Failed to save tax", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleEdit = (tax) => {
    setForm({
      ...tax,
      tax_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    setActiveTab("1");
  };

  const handleView = async (tax) => {
    setViewLoadingRow(tax.tax_Id);
    try {
      // For now, just show the tax details in a modal or alert
      Swal.fire({
        title: "Tax Details",
        html: `
          <div class="text-start">
            <p><strong>Code:</strong> ${tax.tax_Code}</p>
            <p><strong>Name (English):</strong> ${tax.tax_NameEn}</p>
            <p><strong>Name (Arabic):</strong> ${tax.tax_NameAr || 'N/A'}</p>
            <p><strong>Percentage:</strong> ${tax.tax_Perc}%</p>
            <p><strong>Status:</strong> ${tax.tax_Active ? 'Active' : 'Inactive'}</p>
          </div>
        `,
        confirmButtonText: "Close",
      });
    } catch (error) {
      console.error("Error viewing tax:", error);
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadTaxes();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadTaxes();
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentTaxes = taxes.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Tax Management" breadcrumbItem="Spa" />
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">
                  <Calculator className="me-2" size={20} />
                  Tax Management
                </h4>

                <Nav tabs className="nav-tabs-custom">
                  {!isViewOnly && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Tax Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Tax List</span>
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="mt-3">
                  {/* Tax Form Tab */}
                  <TabPane tabId="1">
                    <Form>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label for="tax_NameEn">
                              Tax Name (English) <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id="tax_NameEn"
                              name="tax_NameEn"
                              type="text"
                              value={form.tax_NameEn}
                              onChange={(e) =>
                                setForm({ ...form, tax_NameEn: e.target.value })
                              }
                              placeholder="Enter tax name in English"
                              disabled={isViewOnly}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label for="tax_NameAr">Tax Name (Arabic)</Label>
                            <Input
                              id="tax_NameAr"
                              name="tax_NameAr"
                              type="text"
                              value={form.tax_NameAr}
                              onChange={(e) =>
                                setForm({ ...form, tax_NameAr: e.target.value })
                              }
                              placeholder="Enter tax name in Arabic"
                              disabled={isViewOnly}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label for="tax_Perc">
                              Tax Percentage (%) <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id="tax_Perc"
                              name="tax_Perc"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={form.tax_Perc}
                              onChange={(e) =>
                                setForm({ ...form, tax_Perc: parseFloat(e.target.value) || 0 })
                              }
                              placeholder="Enter tax percentage"
                              disabled={isViewOnly}
                            />
                          </div>
                        </Col>
                        {hasDeletePermission && (
                          <Col md={6}>
                            <div className="mb-3">
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="tax_Active"
                                  checked={form.tax_Active}
                                  onChange={(e) =>
                                    setForm({ ...form, tax_Active: e.target.checked })
                                  }
                                />
                                <label className="form-check-label" htmlFor="tax_Active">
                                  {form.tax_Active ? "Active" : "Inactive"}
                                </label>
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>

                      <Row>
                        <Col>
                          <div className="d-flex gap-2">
                            <Button
                              color="primary"
                              onClick={handleSave}
                              disabled={loading}
                            >
                              {loading ? (
                                <ClipLoader size={16} color="#fff" />
                              ) : (
                                "Save"
                              )}
                            </Button>
                            {isEditing && (
                              <Button
                                color="secondary"
                                onClick={resetForm}
                                disabled={loading}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </TabPane>

                  {/* Tax List Tab */}
                  <TabPane tabId="2">
                    <Row>
                      <Col>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5>Tax List</h5>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setShowFilters(!showFilters)}
                          >
                            {showFilters ? "Hide Filters" : "Show Filters"}
                          </Button>
                        </div>

                        {showFilters && (
                          <Card className="mb-3">
                            <CardBody>
                              <Row>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label for="filter_tax_NameEn">Tax Name</Label>
                                    <Input
                                      id="filter_tax_NameEn"
                                      type="text"
                                      value={filter.tax_NameEn}
                                      onChange={(e) =>
                                        setFilter({ ...filter, tax_NameEn: e.target.value })
                                      }
                                      placeholder="Filter by tax name"
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label for="filter_tax_Perc">Tax Percentage</Label>
                                    <Input
                                      id="filter_tax_Perc"
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={filter.tax_Perc || ""}
                                      onChange={(e) =>
                                        setFilter({ 
                                          ...filter, 
                                          tax_Perc: e.target.value ? parseFloat(e.target.value) : null 
                                        })
                                      }
                                      placeholder="Filter by percentage"
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                                                        <Label for="filter_tax_Active">Status</Label>
                                    <Select
                                      id="filter_tax_Active"
                                      value={
                                        filter.tax_Active === null
                                          ? null
                                          : filter.tax_Active === true
                                          ? { value: true, label: "Active" }
                                          : { value: false, label: "Inactive" }
                                      }
                                                                               onChange={(option) =>
                                          setFilter({
                                            ...filter,
                                            tax_Active: option ? option.value : null,
                                          })
                                        }
                                      options={[
                                        { value: true, label: "Active" },
                                        { value: false, label: "Inactive" },
                                      ]}
                                      isClearable
                                      placeholder="Select status"
                                      styles={selectStyles}
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3 d-flex gap-2" style={{ marginTop: "32px" }}>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={handleApplyFilter}
                                      disabled={loading}
                                    >
                                      Apply Filter
                                    </Button>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={handleClearFilter}
                                      disabled={loading}
                                    >
                                      Clear
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        )}

                        <div className="table-responsive">
                          <Table className="table-nowrap mb-0">
                            <thead className="table-light">
                              <tr>
                                <th scope="col">S.No</th>
                                <th scope="col">Tax Name (English)</th>
                                <th scope="col">Tax Name (Arabic)</th>
                                <th scope="col">Percentage</th>
                                <th scope="col">Status</th>
                                <th scope="col">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    <ClipLoader size={20} color="#556ee6" />
                                  </td>
                                </tr>
                              ) : currentTaxes.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    No taxes found
                                  </td>
                                </tr>
                              ) : (
                                currentTaxes.map((tax, index) => (
                                  <tr key={tax.tax_Id}>
                                    <td>{startIndex + index + 1}</td>
                                    <td>{tax.tax_NameEn}</td>
                                    <td>{tax.tax_NameAr || "N/A"}</td>
                                    <td>{tax.tax_Perc}%</td>
                                    <td>
                                      <span className={`badge ${tax.tax_Active ? 'bg-success' : 'bg-danger'}`}>
                                        {tax.tax_Active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleView(tax)}
                                          disabled={viewLoadingRow === tax.tax_Id}
                                        >
                                          {viewLoadingRow === tax.tax_Id ? (
                                            <ClipLoader size={12} color="#fff" />
                                          ) : (
                                            <Eye size={14} />
                                          )}
                                        </Button>
                                        {hasEditOrHigher && (
                                          <Button
                                            color="warning"
                                            size="sm"
                                            onClick={() => handleEdit(tax)}
                                            disabled={editLoadingRow === tax.tax_Id}
                                          >
                                            {editLoadingRow === tax.tax_Id ? (
                                              <ClipLoader size={12} color="#fff" />
                                            ) : (
                                              <SquarePen size={14} />
                                            )}
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                              <span className="text-muted">
                                Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                              </span>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                              <span className="text-muted">Page size:</span>
                              <Select
                                value={{ value: pageSize, label: pageSize.toString() }}
                                onChange={(option) => handlePageSizeChange(option.value)}
                                options={[
                                  { value: 5, label: "5" },
                                  { value: 10, label: "10" },
                                  { value: 20, label: "20" },
                                  { value: 50, label: "50" },
                                ]}
                                styles={selectStyles}
                                className="w-auto"
                              />
                              <div className="d-flex gap-1">
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => handlePageChange(page - 1)}
                                  disabled={page === 1}
                                >
                                  Previous
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => handlePageChange(page + 1)}
                                  disabled={page === totalPages}
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TaxPage;
